import { ContractAccessType, ContractLimitFilterValue, ContractStatus } from 'app/modules/contracts/contractTypes';
import ContractStatusDot from "../../core/components/StatusDot/ContractStatusDot";
import { getProductIcon } from 'legoland-sdk';
import { useMeQuery, useLimitSchemasQuery, useProductsQuery, UserRole } from 'legoland-sdk/dist/experimental';
import { useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Input, Label, FormGroup, Select, propStyling, tombac, Checkbox as TombacCheckbox } from 'tombac';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from 'tombac-icons';
import { ContractAccessTypeIcon } from "../common/ContractAccessTypeIcon";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Checkbox = styled(TombacCheckbox).withConfig({
  displayName: "ContractsFilter__Checkbox"
})(["", " & > [class^=\"Checkbox__StyledCheckbox\"]{vertical-align:middle;}& > [class^='Checkbox__Label']{align-items:center;display:inline-flex;gap:", ";height:", ";vertical-align:middle;}"], propStyling, tombac.space(1), tombac.unit(24));
const FilterGroupLabel = styled(Label).attrs(() => ({
  as: 'div'
})).withConfig({
  displayName: "ContractsFilter__FilterGroupLabel"
})(["display:block;margin-bottom:", ";"], tombac.space(1));
const SearchInput = styled(Input).withConfig({
  displayName: "ContractsFilter__SearchInput"
})(["flex:1;max-width:", ";background:", ";& > input{border-bottom-right-radius:0;border-top-right-radius:0;}"], tombac.unit(560), tombac.color('neutral', 100));
const defaultCheckboxGroupFilter = [];
const limitValueOptions = [{
  label: 'Any',
  value: ContractLimitFilterValue.Any
}, {
  label: 'None',
  value: ContractLimitFilterValue.None
}];
const booleanLimitValueOptions = [{
  label: 'Any',
  value: ContractLimitFilterValue.Any
}, {
  label: 'True',
  value: ContractLimitFilterValue.True
}, {
  label: 'False',
  value: ContractLimitFilterValue.False
}, {
  label: 'None',
  value: ContractLimitFilterValue.None
}];
function CheckboxGroupFilter({
  filter = defaultCheckboxGroupFilter,
  options,
  onChange
}) {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: options.map(option => {
      return /*#__PURE__*/_jsx(Checkbox, {
        $height: "24u",
        label: option.label,
        checked: filter.indexOf(option.value) > -1,
        onChange: event => {
          if (event.target.checked) {
            onChange([...filter, option.value]);
          } else {
            onChange(filter.filter(r => r !== option.value));
          }
        }
      }, option.value);
    })
  });
}
const ContractsFilter = ({
  filters,
  onChange
}) => {
  const products = useProductsQuery().data?.products;
  const limitSchemas = useLimitSchemasQuery().data?.schemas;
  const meQuery = useMeQuery();
  const role = meQuery.data?.me?.role;
  const [showFilters, setShowFilters] = useState(true);
  const availableProducts = products?.filter(product => role === UserRole.Distributor ? product.id !== 'rer' : true) ?? [];
  const productOptions = availableProducts.map(o => {
    const Icon = getProductIcon(o.id);
    return {
      label: /*#__PURE__*/_jsxs(Box, {
        as: "span",
        $display: "inline-flex",
        $alignItems: "center",
        $gap: "1u",
        children: [/*#__PURE__*/_jsx(Icon, {
          height: "24",
          width: "24"
        }), /*#__PURE__*/_jsx("span", {
          children: o.displayName
        })]
      }),
      value: o.id
    };
  });
  const productsWithLimits = new Set((limitSchemas ?? []).map(schema => schema.productId).filter(id => Boolean(id)));
  const limitProductOptions = productOptions.filter(option => productsWithLimits.has(option.value));
  const selectedLimitProduct = limitProductOptions.find(option => option.value === filters.limitProductId);
  const limitOptions = filters.limitProductId && limitSchemas ? limitSchemas.filter(schema => schema.productId === filters.limitProductId).map(schema => ({
    label: schema.label ?? schema.name,
    value: schema.name,
    type: schema.type
  })).sort((a, b) => a.label.localeCompare(b.label)) : [];
  const selectedLimitName = limitOptions.find(option => option.value === filters.limitName);
  const selectedLimitSchemaType = selectedLimitName?.type;
  const valueOptions = selectedLimitSchemaType === 'BOOLEAN' ? booleanLimitValueOptions : limitValueOptions;
  const selectedLimitValue = valueOptions.find(option => option.value === filters.limitValue);
  return /*#__PURE__*/_jsxs("div", {
    "data-sentry-component": "ContractsFilter",
    "data-sentry-source-file": "ContractsFilter.tsx",
    children: [/*#__PURE__*/_jsxs(Box, {
      $width: "100%",
      $display: "flex",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ContractsFilter.tsx",
      "data-sentry-element": "Box",
      children: [/*#__PURE__*/_jsx(SearchInput, {
        onChange: e => onChange({
          ...filters,
          searchValue: e.target.value
        }),
        placeholder: "Search contract",
        prepend: /*#__PURE__*/_jsx(SearchIcon, {}),
        type: "text",
        value: filters.searchValue ?? '',
        "data-sentry-element": "SearchInput",
        "data-sentry-source-file": "ContractsFilter.tsx",
        "data-sentry-element": "SearchInput"
      }), /*#__PURE__*/_jsx(Button, {
        $borderLeft: "none",
        $borderBottomLeftRadius: "0",
        $borderTopLeftRadius: "0",
        prepend: showFilters ? /*#__PURE__*/_jsx(ChevronUpIcon, {}) : /*#__PURE__*/_jsx(ChevronDownIcon, {}),
        onClick: () => setShowFilters(b => !b),
        "data-sentry-element": "Button",
        "data-sentry-source-file": "ContractsFilter.tsx",
        "data-sentry-element": "Button",
        children: "Filters"
      })]
    }), showFilters && /*#__PURE__*/_jsxs(ContractsFilters, {
      children: [/*#__PURE__*/_jsxs(ContractsFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Status"
        }), /*#__PURE__*/_jsx(ContractsFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.statuses,
            options: Object.entries(ContractStatus).map(o => ({
              label: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(ContractStatusDot, {
                  status: o[1]
                }), " ", o[0]]
              }),
              value: o[1]
            })),
            onChange: statuses => {
              onChange({
                ...filters,
                statuses
              });
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(ContractsFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Access"
        }), /*#__PURE__*/_jsx(ContractsFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.accessTypes,
            options: Object.entries(ContractAccessType).filter(o => role === UserRole.Distributor ? o[1] === ContractAccessType.Evaluation || o[1] === ContractAccessType.Paid : true).map(o => ({
              label: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(ContractAccessTypeIcon, {
                  accessType: o[1]
                }), " ", o[0]]
              }),
              value: o[1]
            })),
            onChange: accessTypes => onChange({
              ...filters,
              accessTypes
            })
          })
        })]
      }), products && /*#__PURE__*/_jsxs(ContractsFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Products"
        }), /*#__PURE__*/_jsx(ContractsFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.products,
            options: productOptions,
            onChange: products => onChange({
              ...filters,
              products
            })
          })
        })]
      }), products && role && [UserRole.Admin, UserRole.Support].includes(role) && /*#__PURE__*/_jsxs(ContractsFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Limits"
        }), /*#__PURE__*/_jsxs(LimitFiltersGroupInputs, {
          children: [/*#__PURE__*/_jsx(FormGroup, {
            label: "Product",
            children: /*#__PURE__*/_jsx(Select, {
              isClearable: true,
              $width: "250u",
              onChange: option => {
                onChange({
                  ...filters,
                  limitProductId: option?.value,
                  limitName: undefined,
                  limitValue: undefined
                });
              },
              options: limitProductOptions,
              value: selectedLimitProduct ?? null
            })
          }), /*#__PURE__*/_jsx(FormGroup, {
            label: "Limit",
            children: /*#__PURE__*/_jsx(Select, {
              isClearable: true,
              isDisabled: !filters.limitProductId,
              $width: "250u",
              onChange: option => {
                onChange({
                  ...filters,
                  limitName: option?.value,
                  limitValue: undefined
                });
              },
              options: limitOptions,
              value: selectedLimitName ?? null
            })
          }), /*#__PURE__*/_jsx(FormGroup, {
            label: "Value",
            children: /*#__PURE__*/_jsx(Select, {
              isClearable: true,
              isDisabled: !filters.limitName,
              $width: "250u",
              onChange: option => {
                onChange({
                  ...filters,
                  limitValue: option?.value
                });
              },
              options: selectedLimitSchemaType === 'BOOLEAN' ? booleanLimitValueOptions : limitValueOptions,
              value: selectedLimitValue ?? null
            })
          })]
        })]
      })]
    })]
  });
};
const ContractsFilters = styled.div.withConfig({
  displayName: "ContractsFilter__ContractsFilters"
})(["display:flex;flex-wrap:wrap;margin-bottom:", ";"], tombac.space(3));
const ContractsFiltersGroup = styled.div.withConfig({
  displayName: "ContractsFilter__ContractsFiltersGroup"
})(["margin:", ";padding:", ";border-right:", ";margin-bottom:", ";"], tombac.space(3, 0), tombac.space(0, 3), tombac.parse('1u solid --neutral-300'), tombac.space(3));
const ContractsFiltersGroupInputs = styled.div.withConfig({
  displayName: "ContractsFilter__ContractsFiltersGroupInputs"
})(["display:grid;grid-auto-flow:column;grid-gap:0.8rem;grid-template-rows:repeat(6,1fr);"]);
const LimitFiltersGroupInputs = styled.div.withConfig({
  displayName: "ContractsFilter__LimitFiltersGroupInputs"
})(["display:grid;gap:", ";grid-template-columns:1fr;"], tombac.space(1));
export default ContractsFilter;